'use client';

import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Upload, Download, Copy, Trash2, ArrowRight, ArrowDown, Code, Settings, Check, X } from 'lucide-react';
import { saveAs } from 'file-saver';
import { cn } from '@/lib/utils';
import * as yaml from 'js-yaml';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { yamlFormatterContent } from '../../data/yaml-formatter-content';
import Link from 'next/link';

interface CodeEditorProps {
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    placeholder?: string;
    className?: string;
    hasError?: boolean;
}

const CodeEditor = ({
    value,
    onChange,
    readOnly,
    placeholder,
    className,
    hasError = false
}: CodeEditorProps) => {
    return (
        <div className={cn(
            "relative flex h-full w-full overflow-hidden rounded-md border bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
            hasError ? 'border-destructive' : '',
            className
        )}>
            <Textarea
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                readOnly={readOnly}
                className="h-full w-full resize-none border-0 focus-visible:ring-0 rounded-none p-4 font-mono text-sm leading-6"
                placeholder={placeholder}
                spellCheck={false}
            />
            {hasError && (
                <div className="absolute top-2 right-2 p-1 bg-destructive/10 rounded-full">
                    <X className="h-4 w-4 text-destructive" />
                </div>
            )}
        </div>
    );
};

interface FormatOptions {
    indent: number;
    lineWidth: number;
    sortKeys: boolean;
    noRefs: boolean;
}

const defaultOptions: FormatOptions = {
    indent: 2,
    lineWidth: 80,
    sortKeys: false,
    noRefs: true
};

const formatYaml = (yamlString: string, options: FormatOptions): { formatted: string; error?: string } => {
    try {
        if (!yamlString.trim()) return { formatted: '', error: 'Empty YAML content' };

        // First parse to ensure valid YAML
        const parsed = yaml.load(yamlString);

        // Then stringify with formatting options
        const formatted = yaml.dump(parsed, {
            indent: options.indent,
            lineWidth: options.lineWidth,
            sortKeys: options.sortKeys,
            noRefs: options.noRefs,
            skipInvalid: true,
            noCompatMode: true
        });

        return { formatted };
    } catch (error) {
        console.error('Error formatting YAML:', error);
        return {
            formatted: yamlString,
            error: error instanceof Error ? error.message : 'An error occurred while formatting YAML'
        };
    }
};

const validateYaml = (yamlString: string): boolean => {
    try {
        if (!yamlString.trim()) return true;
        yaml.load(yamlString);
        return true;
    } catch (error) {
        return false;
    }
};

export default function YamlFormatter() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [options, setOptions] = useState<FormatOptions>(defaultOptions);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [isValid, setIsValid] = useState(true);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    const formatYamlHandler = () => {
        const isValidYaml = validateYaml(input);
        setIsValid(isValidYaml);

        if (!isValidYaml) {
            toast({
                title: "Invalid YAML",
                description: "The input contains invalid YAML syntax. Please check and try again.",
                variant: "destructive",
            });
            return;
        }

        try {
            if (!input.trim()) {
                toast({
                    title: "Error",
                    description: "Please enter some YAML to format",
                    variant: "destructive",
                });
                return;
            }

            const { formatted, error } = formatYaml(input, options);

            if (error) {
                throw new Error(error);
            }

            setOutput(formatted);

            toast({
                title: "Success",
                description: "YAML formatted successfully!",
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "An error occurred while formatting YAML",
                variant: "destructive",
            });
            console.error('Error formatting YAML:', error);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setInput(content);
            setIsValid(validateYaml(content));
            toast({
                title: "File Uploaded",
                description: `Loaded ${file.name}`,
            });
        };
        reader.readAsText(file);
    };

    const handleDownload = () => {
        if (!output) return;
        const blob = new Blob([output], { type: "text/yaml;charset=utf-8" });
        saveAs(blob, "formatted.yaml");
        toast({
            title: "Downloaded",
            description: "File saved as formatted.yaml",
        });
    };

    const copyToClipboard = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        toast({
            title: "Copied!",
            description: "Formatted YAML copied to clipboard",
        });
    };

    const clearAll = () => {
        setInput('');
        setOutput('');
        setIsValid(true);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const sampleYaml = `# Sample YAML Document
person:
  name: John Doe
  age: 30
  contact:
    email: john@example.com
    phone: "+1-555-123-4567"
  addresses:
    - type: home
      street: 123 Main St
      city: Anytown
      state: CA
      zip: '12345'
    - type: work
      company: Acme Inc
      street: 456 Work Ln
      city: Business City
      state: CA
      zip: '98765'
  skills:
    - name: JavaScript
      level: expert
    - name: TypeScript
      level: advanced
    - name: Python
      level: intermediate
  active: true
  metadata:
    created: 2023-01-15T10:30:00Z
    last_updated: 2023-11-22T08:00:00Z`;

    const loadSample = () => {
        setInput(sampleYaml);
        setIsValid(true);
    };

    return (
        <div className="container mx-auto p-4 max-w-[1400px]">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <ToolPageIntro title="YAML Formatter">
                Format and beautify YAML code with our free online tool. Clean up messy YAML files and improve code readability with proper indentation.
            </ToolPageIntro>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsOptionsOpen(!isOptionsOpen)}
                        className="gap-2"
                    >
                        <Settings className="h-4 w-4" />
                        {isOptionsOpen ? 'Hide Options' : 'Format Options'}
                    </Button>
                </div>
            </div>

            {/* Options Panel */}
            {isOptionsOpen && (
                <Card className="mb-6">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg">Formatting Options</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Indentation</label>
                            <select
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.indent}
                                onChange={(e) => setOptions({ ...options, indent: parseInt(e.target.value) || 2 })}
                            >
                                <option value="2">2 spaces</option>
                                <option value="4">4 spaces</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none">Line Width</label>
                            <input
                                type="number"
                                min="20"
                                max="200"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={options.lineWidth}
                                onChange={(e) => setOptions({ ...options, lineWidth: parseInt(e.target.value) || 80 })}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none flex items-center">
                                <input
                                    type="checkbox"
                                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={options.sortKeys}
                                    onChange={(e) => setOptions({ ...options, sortKeys: e.target.checked })}
                                />
                                Sort Keys Alphabetically
                            </label>
                            <p className="text-xs text-muted-foreground">
                                Sort object keys alphabetically
                            </p>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium leading-none flex items-center">
                                <input
                                    type="checkbox"
                                    className="mr-2 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    checked={options.noRefs}
                                    onChange={(e) => setOptions({ ...options, noRefs: e.target.checked })}
                                />
                                Disable References
                            </label>
                            <p className="text-xs text-muted-foreground">
                                Disable YAML references and anchors
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-250px)]">
                {/* Input Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-full">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <CardTitle className="flex justify-between items-center">
                            <span>Input YAML</span>
                            <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={loadSample} className="text-muted-foreground">
                                    Sample
                                </Button>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept=".yaml,.yml,.txt"
                                    className="hidden"
                                    id="file-upload"
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="text-muted-foreground"
                                >
                                    <Upload className="h-4 w-4 mr-1" /> Upload
                                </Button>
                                <Button variant="ghost" size="sm" onClick={clearAll} className="text-muted-foreground hover:text-destructive">
                                    <Trash2 className="h-4 w-4" /> Clear
                                </Button>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 relative overflow-hidden">
                        <CodeEditor
                            value={input}
                            onChange={(value) => {
                                setInput(value);
                                setIsValid(validateYaml(value) || value.trim() === '');
                            }}
                            placeholder="Paste your YAML here, upload a file, or try the sample..."
                            className="border-0 rounded-none"
                            hasError={!isValid && input.trim() !== ''}
                        />
                    </CardContent>
                </Card>

                {/* Action Section */}
                <div className="flex lg:flex-col justify-center items-center gap-4 py-4 lg:py-0">
                    <Button
                        onClick={formatYamlHandler}
                        size="lg"
                        className="rounded-full w-16 h-16 lg:w-20 lg:h-20 p-0 shadow-lg hover:scale-105 transition-transform"
                        title="Format YAML"
                        disabled={!isValid && input.trim() !== ''}
                    >
                        <ArrowRight className="h-8 w-8 hidden lg:block" />
                        <ArrowDown className="h-8 w-8 lg:hidden" />
                    </Button>
                </div>

                {/* Output Section */}
                <Card className="flex-1 flex flex-col min-h-[500px] lg:h-full">
                    <CardHeader className="pb-3 border-b bg-muted/20">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <CardTitle className="text-lg font-medium">Formatted Output</CardTitle>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="icon" onClick={copyToClipboard} disabled={!output} title="Copy">
                                    <Copy className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon" onClick={handleDownload} disabled={!output} title="Download">
                                    <Download className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 p-0 bg-muted/30 relative overflow-hidden">
                        <CodeEditor
                            value={output}
                            readOnly
                            placeholder="Formatted YAML will appear here..."
                            className="border-0 rounded-none bg-transparent"
                        />
                    </CardContent>
                </Card>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={yamlFormatterContent.title}
                description={yamlFormatterContent.description}
                content={yamlFormatterContent.content}
                faqs={yamlFormatterContent.faqs}
                relatedTools={yamlFormatterContent.relatedTools}
            />

            <Toaster />
        </div>
    );
}
